/*
 * Copyright 2025 Intuition Robotics ltd
 */
import type {ApiWithBody, ApiWithQuery} from "@intuitionrobotics/thunderstorm";
import {BadImplementationException} from "@intuitionrobotics/ts-common";
import type {DB_Contact} from "./db-contact.js";
import type {UnitIdWasInUse} from "../shared/errors.js";

export interface DBActivationsInterface
    extends ActivationsInterface {
    id: number;
}

export interface ActivationsInterface
    extends BaseActivationsInterface {
    registration_date?: number;
    som_pk: string | null;
    tablet_pk: string | null;
}

export interface BaseActivationsInterface
    extends ActivationUniqueness {
    id?: number;
    pincode: string;
    activated: boolean;
    auth_type: AuthType;
    blame: string;
    replacement_tablet_device_id: string | null;
}

export interface ActivationUniqueness {
    agent_id: string;
    som_device_id: string;
    tablet_device_id: string;
}

export interface ActivationsHistoryInterface
    extends BaseActivationsInterface {
    activation_id: number,
    description: string,
    modification_date?: number
}

export type ActivationForm = Partial<Body_Activation>

export type ActivationOverridesCheck = {
    skipHistoryCheck?: boolean;
    skipInventoryCheck?: boolean;
}

export type Body_Activation = Body_Activation_Metadata & {
    unitId: string
    somSerial?: string
    tabletSerial?: string
    replacementTabletSerial?: string
    authType: AuthType
    birthday?: number // Jan 1, 1970 (ms)
    cloneFromUnitId?: string
} & ActivationOverridesCheck;

export type Body_RenameActivation = Body_Activation & {
    originalUnitId: string,
    user_comment?: string
}

export type Body_Activation_Metadata = {
    firstName: string
    lastName?: string,
    gender?: string,
    middleName?: string,
    phoneNumber: string,
    areaCode: string,
    email?: string,
    country: string
    city: string
    state: string
    region: string
    streetAddress?: string,
    userGroup: string,
    subUserGroup?: string,
    alias: string,
    postalCode?: string,
    extendedAddress?: string
    birthday?: number // Jan 1, 1970 (ms)
    organizationId?: string
    physicianId?: string
    careManagerId?: string
    anniversaryDate?: number
    locale?: string // New field for creation only
}

export const authType = {
    AUTO: "Pre Activated",
    BIRTHDAY: "Birthdate",
    PINCODE: "Activation Code"
} as const;

export type AuthType = keyof typeof authType;
export type AuthVal = typeof authType[AuthType];

export const getAuthKey = (k: AuthVal): AuthType => {
    const entries = Object.entries(authType);
    for (const [key, value] of entries) {
        if (value === k)
            return key as AuthType;
    }
    throw new BadImplementationException("Invalid auth type " + k)
};

export type Response_ListActivation = DBActivationsInterface[]
export type Response_ListActivationHistory = ActivationsHistoryInterface[]

export type PocData = {
    firstName: string,
    lastName: string,
    phoneNumber: string,
    email: string,
    gender: string,
    relation: string,
    isCaregiver: string
}

export type Body_KasperoActivation = Body_Activation & { blame: string } & { pocData?: PocData }

export type Body_KasperoRenameActivation = Body_RenameActivation & { blame: string }

export type Body_KasperoPostRegistration = {
    unitId: string
    contactId: string
    somSerial: string
    tabletSerial: string
    blame: string
}

export type Body_GetActivationsByAgentIds = { agentIds: string[] }
export type Body_UpdateActivation = ActivationsInterface & { id: number, birthday?: number, user_comment?: string }

type ActivationWithMetadata = (ActivationsInterface & { id: number }) | (Partial<ActivationsInterface> & { id: never })
export type Body_UpdateActivationWithMetadata =
    Body_Activation_Metadata
    & ActivationWithMetadata
    & ActivationOverridesCheck

export enum HistoryActions {
    CREATED = "CREATED",
    UPDATED = "UPDATED",
    DELETED = "DELETED",
}

export type Response_Activation = {
    contact: DB_Contact<'agentUser'>
    // No pincode if the activation creation is postponed
    pincode?: string
    timezone?: string
};

export type Response_PostRegistration = {
    contact: DB_Contact<'agentUser'>
    // No pincode if the activation creation is postponed
    pincode: string
};

export type Body_DeleteActivation = ActivationUniqueness & {
    blame: string,
    input_date?: number,
    user_comment?: string
}

export type Body_CloneUnit = {
    sourceUnit: string,
    targetUnit: string
}

export type QueryParams_GetHistoryList = {
    description?: string
    agent_id?: string
}

export type ApiRegisterActivation = ApiWithBody<"/v1/unit/activations/registration", Body_Activation, Response_Activation, UnitIdWasInUse>
export type ApiRenameActivation = ApiWithBody<"/v1/unit/activations/rename", Body_RenameActivation, Response_Activation, UnitIdWasInUse>
export type ApiListActivations = ApiWithQuery<"/v1/unit/activations/list", Response_ListActivation>
export type ApiListActivationsHistory = ApiWithQuery<"/v1/unit/activations/history-list", Response_ListActivationHistory, QueryParams_GetHistoryList>
export type ApiListDeletedActivationsHistory = ApiWithQuery<"/v1/unit/activations/deleted-list", Response_ListActivation>
export type ApiSupportListActivations = ApiWithBody<"/v1/unit/activations/list", Body_GetActivationsByAgentIds, Response_ListActivation>
export type ApiSupportListActivationsV2 = ApiWithBody<"/v2/unit/activations/list", Body_GetActivationsByAgentIds, Response_ListActivation>
export type ApiGetActivationsByAgentIds = ApiWithBody<"/v1/unit/activations/get-by-agent-ids", Body_GetActivationsByAgentIds, Response_ListActivation>
export type ApiUpdateActivations = ApiWithBody<"/v1/unit/activations/update", Body_UpdateActivation, void>
export type ApiUpdateWithMetadataActivations = ApiWithBody<"/v1/unit/activations/update-with-metadata", Body_UpdateActivationWithMetadata, void>
export type ApiDeleteActivations = ApiWithBody<"/v1/unit/activations/delete", Body_DeleteActivation, void>
export type BodyLabTest = {
    serial: string,
    code: string,
    firstName: string,
    lastName: string,
    phoneNumber: string
    longitude?: number,
    latitude?: number
};
export type ApiActivationsLabTest = ApiWithBody<"/v1/unit/activations/lab-test", BodyLabTest, ActivationsInterface>
export type ApiCloneUnit = ApiWithBody<"/v1/unit/activations/clone-unit", Body_CloneUnit, string>
export type ApiGetActivationsBySerial = {
    url: "/v1/unit/activations/get-by-serial";
    response: DBActivationsInterface | undefined;
    body: {
        serial: string
    };
    queryParams: {};
    error: {}
};

export type KasperoApiRegisterActivation = ApiWithBody<"/v1/unit/activations/registration", Body_KasperoActivation, Response_Activation>
export type KasperoApiRenameActivation = ApiWithBody<"/v1/unit/activations/rename", Body_KasperoRenameActivation, Response_Activation>
export type KasperoApiPostRegisterActivation = ApiWithBody<"/v1/unit/activations/post-registration", Body_KasperoPostRegistration, Response_PostRegistration>
